import { useEffect, useState } from 'react';
import { MenuItem, CircularProgress, Select } from '@mui/material';
import { tenantService } from '../../services/tenant/tenantService';
import { useAuth } from '../../../modules/auth/context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

type TenantSelectIfAdminProps = {
  value?: string;
  onChange?: (tenantId: string) => void;
  label?: string;
  disabled?: boolean;
};

export function TenantSelectIfAdmin({
  onChange,
  label = 'Empresa',
  disabled = false,
}: TenantSelectIfAdminProps) {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const { selectedTenant, handleSetSelectedTenant } = useTenant();
  const isMultiTenant = user?.tenants.length > 1;
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    if (isAdmin) {
      if (isMultiTenant) {
        // Pega os tenants do próprio usuário
        setTenants(user.tenants || []);
        if (!selectedTenant && user.tenants && user.tenants.length > 0) {
          handleSetSelectedTenant(user.tenants[0].id);
        }
        setLoading(false);
      } else {
        setLoading(true);
        tenantService
          .list()
          .then((res) => {
            setTenants(res.data);
            // Set first company as default if no tenant is selected yet
            if (!selectedTenant && res.data.length > 0) {
              handleSetSelectedTenant(res.data[0].id);
            }
          })
          .finally(() => setLoading(false));
      }
    }
  }, [isAdmin, isMultiTenant, selectedTenant, handleSetSelectedTenant, user.tenants]);

  if (!isAdmin) return null;

  const handleChange = async (e: any) => {
    const id = e.target.value.trim(); 
    const selectedTenantName = tenants.find(t => t.id === id)?.name;
    
    try {
      handleSetSelectedTenant(id);
      if (onChange) onChange(id);

      // Pega o nome da rota atual (ex: /orders, /users, etc)
      const currentRoute = location.pathname.split('/')[1];
      
      // Recarrega os dados da rota atual
      await queryClient.invalidateQueries({ queryKey: [currentRoute] });
    } catch (error) {
    }
  };

  return (
    <Select
      value={selectedTenant || ''}
      onChange={handleChange}
      fullWidth
      size="medium"
      displayEmpty
      sx={{
        width: '100%',
        fontSize: 16,
        background: '#fff',
        color: '#222',
        fontWeight: 500,
        mt: 0.5,
        mb: 0.5,
        borderRadius: 2,
        textAlign: 'center',
        '& .MuiSelect-select': {
          py: 1.2,
          px: 1.5,
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      inputProps={{ 'aria-label': label }}
      disabled={disabled || loading}
    >
      <MenuItem value="" disabled sx={{ justifyContent: 'center', textAlign: 'center' }}>
        <span style={{ color: '#888', width: '100%', display: 'block', textAlign: 'center' }}>Sua Empresa</span>
      </MenuItem>
      {loading ? (
        <MenuItem value="" disabled sx={{ justifyContent: 'center', textAlign: 'center' }}>
          <CircularProgress size={20} />
        </MenuItem>
      ) : (
        tenants.map((tenant) => (
          <MenuItem key={tenant.id} value={tenant.id} sx={{ justifyContent: 'center', textAlign: 'center' }}>
            {tenant.name}
          </MenuItem>
        ))
      )}
    </Select>
  );
}